import { createContext, useRef, useEffect } from 'react';
import { dBConn, setTimeFormat, API_ADRWS, AM_socketId } from './API_GET';

const InfoContext = createContext();

const InfoProvider = ({ initialData, amKRASTRef, amKRASTHeaderRef, children }) => {
    /*const [info, setInfo] = useState({
        'download': {status: 'none'},
        'upload': {status: 'none'},
        'scriptsubtitles': {
          status: 'none',
          address: ''
        },
    });*/

    const info = useRef({
        download: {status: 'none'},
        upload: {status: 'none'},
        scriptsubtitles: {
          status: 'none',
          address: ''
        }
    });

    const portalRef = useRef(null);
    const portalOpen = useRef(null);
    
    const userData = useRef(initialData);
    const compRefs = useRef({});
    const deliveryDashData = useRef({});
    
    const refreshComponents = (newUserData, refreshKey) => {
        const refreshRefs = new Set();

        if(refreshKey) {
            compRefs.current?.[refreshKey]?.forEach(r => refreshRefs.add(r))
        }
        
        if(newUserData) {
            Object.entries(newUserData).forEach(([k, v]) => {
                if(k === 'permissions') {
                    if(JSON.stringify(v) !== JSON.stringify(userData.current.permissions)) {               
                        userData.current?.permissionsMap?.forEach(p => {
                            if(userData.current.permissions?.[p] !== v?.[p]) {
                                compRefs.current?.[p]?.forEach(r => refreshRefs.add(r));
                            }
                        })
                    }
                } else {
                    if(v !== userData.current[k]) compRefs.current?.[k]?.forEach(r => refreshRefs.add(r));
                }
            })
            
            userData.current = newUserData;
            setTimeFormat(newUserData.timeformat12 === 'true');
        }

        refreshRefs.forEach(r => { r?.() })
    }
  
    const setInfoData = (data) => {
        info.current = data;
        window.dispatchEvent(new CustomEvent('infoDataChanged', { detail: data }));
    };

    const wsConnected = useRef(true);
    const webSocketFunctions = useRef({});    
    const openSubscriptions = useRef({});
    const socket = useRef(null);
    const intervalId = useRef(null);
    const syncInvitationsSent = useRef([]);
    const syncInvitationsReceived = useRef([]);
    const syncClientId = useRef(null);
    const syncSentFn = useRef(null);
    const syncReceivedFn = useRef(null);
    const syncWithReaper = useRef(false);
    const addBackgroundJob = useRef(null);
    
    const reconnectToWs = useRef(null);

    const sendMsgToWebSocket = (data, get = false) => {
        if(!data) return;
        if(!get) {
            data['broadcast'] = true;
            data['socketId'] = AM_socketId();
        }
        if(socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(data));
    }
    
    const subscribeToWebSocket = (data, type, subscribe) => {
        if(!subscribe) {
            delete openSubscriptions.current?.[type];
        } else {
            openSubscriptions.current[type] = data;
        }
        if(socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(data));
        if(!AM_socketId()) { reconnectToWs.current?.() }
    }

    useEffect(() => {
        const closeSyncConnections = () => {
            const activeSyncUsers = syncInvitationsSent.current.filter((u => u.isChecked));
            activeSyncUsers.forEach(u => { u.isChecked = false })
            const msg = {
                updateType: 'syncInvitation', 
                userIds: activeSyncUsers, 
                sender: {clientId: AM_socketId(), name: userData.current.useralias},
            };
            sendMsgToWebSocket(msg);
        }
        window.addEventListener("unload", closeSyncConnections);
        return () => {
            window.removeEventListener("unload", closeSyncConnections);
        }
    }, []);
    
    useEffect(() => {
        
        wsConnected.current = false;
        
        const setClearInterval = (value) => {
            if(intervalId.current) {
                clearInterval(intervalId.current);
                intervalId.current = null;
            }
            if (!value) { return }
            intervalId.current = setInterval(() => {  
                if(wsConnected.current) return;
                const rootElement = document.getElementById('root');
                if (rootElement && rootElement.innerHTML.trim() === '') return;
                if(!AM_socketId()) { webSocketConnect() } 
                //update userData
                dBConn.get('refreshMenu=1').then((response) => refreshComponents(response));
                //update script
                webSocketFunctions.current?.script?.(null, true);
            }, 60000);
        };
        
        const webSocketConnect = () => {
            if(socket.current?.readyState === WebSocket.OPEN || window.location.hostname === "dev.kantanaudiomate.com") return
            if(!userData.current?.id) return;

            openSubscriptions.current['userData'] = {
                updateType: "userData", 
                userId: userData.current.id, 
                userName: userData.current.useralias, 
                siteIds: Object.keys(userData.current.sites)
            };
        
            socket.current = new WebSocket(API_ADRWS('audiomate')); 
    
            socket.current.onopen = () => {
                wsConnected.current = true;
                setClearInterval(false);
                console.log('Connection established with WebSocket server');
                Object.values(openSubscriptions.current).forEach(s => socket.current.send(JSON.stringify(s)));
            };
            
            socket.current.onmessage = (event) => {
                let message = null;
                try { message = JSON.parse(event.data) } catch (error) { return }
                
                if(message.updateType === 'userConnected') { 
                    AM_socketId(true, message.userId);
                    return;
                };
                
                if(AM_socketId() === message.userId) { return } 
                
                if(message.sub === 'script') {
                    webSocketFunctions.current?.script?.(message);
                } else if(message.sub === 'scheduler') {
                    webSocketFunctions.current?.scheduler?.(message);
                } else if(message.sub === 'xloc') {
                    webSocketFunctions.current?.xloc?.(message);
                } else if(message.sub === 'scriptSync') {
                    if(syncClientId.current !== message.userId) return;
                    webSocketFunctions.current?.scriptSync?.(message.scriptDataId, message.scriptLangId);
                } else if(message.sub === 'userData') {
                    dBConn.get('refreshMenu=1').then((response) => refreshComponents(response));
                } else if(message.sub === 'getConnectedUsers') {
                    const selected = syncInvitationsSent.current.filter(u => u.isChecked);
                    syncInvitationsSent.current.splice(0, syncInvitationsSent.current.length);
                    const sorted = message.users.sort((a,b) => a.name.localeCompare(b.name));
                    sorted.forEach(u => {
                        u.isChecked = selected.some(c => c?.id === u.id);
                        syncInvitationsSent.current.push(u);
                    });
                    syncSentFn.current?.();
                } else if(message.sub === 'checkInvitationsStatus') {
                    for(let i = syncInvitationsReceived.current.length - 1; i >= 0; i--) {
                        if(!message.users.includes(syncInvitationsReceived.current[i].clientId)) {
                            syncInvitationsReceived.current.splice(i, 1);
                        } 
                    }                    
                    syncReceivedFn.current?.();
                } else if(message.sub === 'syncInvitation') {
                    if(message?.sender?.clientId === AM_socketId()) return;
                    const user = syncInvitationsReceived.current.find(u => u.clientId === message?.sender?.clientId);
                    const clientId = message?.sender?.clientId ?? null;
                    if(message?.add && clientId) {
                        if(!user) {
                            message.sender.isChecked = false
                            syncInvitationsReceived.current.push(message.sender);
                        };
                    } else if(user) {
                        if(clientId === syncClientId.current) {
                            syncWithReaper.current = false;
                            syncClientId.current = null;   
                        }
                        const index = syncInvitationsReceived.current.indexOf(user);
                        if (index > -1) syncInvitationsReceived.current.splice(index, 1);
                    }
                    syncReceivedFn.current?.();
                }                 
            }; 
            
            socket.current.onclose = () => {
                AM_socketId(true, null);
                wsConnected.current = false;
                setClearInterval(true);
                console.log('Connection closed with WebSocket server');
            };
            
        }
        
        reconnectToWs.current = webSocketConnect; 
        webSocketConnect();
        
        return () => {
            wsConnected.current = true;
            setClearInterval(false);
            if(socket.current?.readyState === WebSocket.OPEN) { socket.current?.close() }
        }
        
    }, []);  

    return (
        <InfoContext.Provider value={
            { 
                userData, 
                compRefs, 
                refreshComponents,
                info,
                setInfoData,
                portalRef,
                portalOpen,
                webSocketFunctions,
                subscribeToWebSocket,
                sendMsgToWebSocket,
                syncInvitationsSent,
                syncInvitationsReceived,
                syncClientId,
                syncSentFn,
                syncReceivedFn,
                syncWithReaper,
                deliveryDashData,
                amKRASTRef,
                amKRASTHeaderRef,
                addBackgroundJob
            }
        }>
            {children}
        </InfoContext.Provider>
    );
};

export { InfoContext, InfoProvider };

/* instruction to use context 


//////////////////// for download ///////////////////////////

//  1. import the script. Located in ...
//    import  {InfoContext}  from './AM_BackGroundOP';
//  2. import the context in the component 
//    const { info, setInfoData } = useContext(InfoContext);
//  3. key of array for download

    [
      {
          to: "12345.wav", from: "71/source/13384.wav"
      },
      {
          to: "12346.wav", from: "71/source/13388.wav"
      }
    ]


//  4. start the download

//  multiple file (send just one object in the array) 

    setInfoData(previous => ({
        ...previous,
        download: {
            status: 'request',
            outputFilename: 'zipname.zip', 
            fileList: arrayOfFiles
        }
    }))

//  single file 

    setInfoData(previous => ({
        ...previous,
        download: {
            status: 'request',
            outputFilename: 'audiofile.wav', // or nothing for tak the original name  
            fileList: arrayOfFiles // just one object in the array
        }
    }))


*/